import { useRuntimeConfig } from "nuxt/app";
import { useApiCall } from "~/composables/useApiCall";
import { RailsApiResponseType } from "~/types/RailsApiResponseType";
import { useUserStore } from "~/store/user";
import { SESSION_VALIDATED_AT_KEY, isSessionStale } from "~/constants/session";

// global guard (module scoped, persists between calls)
let sessionPromise: Promise<string> | null = null;

const extractData = (response: any): RailsApiResponseType | null => {
  return response && response.data && response.data._rawValue
    ? response.data._rawValue
    : null;
};

const markSessionValidated = (): void => {
  localStorage.setItem(SESSION_VALIDATED_AT_KEY, Date.now().toString());
};

export const clearSessionData = (): void => {
  localStorage.removeItem("user");
  localStorage.removeItem("organization");
  localStorage.removeItem(SESSION_VALIDATED_AT_KEY);
  localStorage.removeItem("currentSearchDashboard");
  localStorage.removeItem("currentProfile");
  localStorage.removeItem("language");
  localStorage.removeItem("userApplications");
  localStorage.removeItem("cmConfig");
  localStorage.removeItem("attemptedRoute");
};

export const setSessionData = (): Promise<string> => {
  if (sessionPromise) {
    return sessionPromise;
  }

  sessionPromise = (async () => {
    try {
      // Check if session data is already initialized in localStorage
      const existingUser = localStorage.getItem("user");
      const existingOrg = localStorage.getItem("organization");

      // Only skip API validation if session exists AND is not stale (< 30 minutes old)
      if (existingUser && existingOrg && !isSessionStale()) {
        // Session already exists and is fresh, just ensure user applications are loaded
        const userStore = useUserStore();
        if (userStore.userApplications.length === 0) {
          userStore.fetchUserApplications().catch((error) => {
            // eslint-disable-next-line no-console
            console.warn("Failed to fetch user applications:", error);
          });
        }
        return "Ok";
      }

      // Session is stale or missing - validate with API

      const sessionResponse = await useApiCall("/session_data", {
        method: "GET",
      });

      if (!sessionResponse) {
        return "RequestAborted";
      }

      const errorCode = sessionResponse.error.value?.data?.errors?.[0]?.code;
      const statusCode = sessionResponse.error.value?.statusCode;

      if (statusCode === 403 && errorCode === "no_application_access") {
        return "NoApplicationAccess";
      }

      if (statusCode === 403 && errorCode === "subscription_expired") {
        return "NotAuthorized";
      }

      if (statusCode === 401 && errorCode === "revoked_token") {
        return "TokenRevoked";
      }

      if (statusCode === 401) {
        return "NotAuthorized";
      }

      // Legacy fallback for 500 errors
      if (
        statusCode === 500 &&
        String(sessionResponse.error.value?.data.exception).includes(
          "NotAuthorizedError",
        )
      ) {
        return "NotAuthorized";
      }

      const sessionData: RailsApiResponseType | null =
        extractData(sessionResponse);
      if (sessionData) {
        localStorage.setItem(
          "user",
          JSON.stringify({
            id: sessionData.data.id,
            ...sessionData.data.attributes,
          }),
        );

        try {
          const userStore = useUserStore();
          userStore.fetchUserApplications().catch((error) => {
            // eslint-disable-next-line no-console
            console.warn("Failed to fetch user applications:", error);
          });
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn(
            "Failed to fetch user applications during session init:",
            error,
          );
          // Non-critical error - don't block session initialization
        }

        const config = useRuntimeConfig();

        if (config.public.CM_API_HOST && config.public.CM_PARTNER_TOKEN) {
          // Store the CM environment variables in localStorage to be used in cm-reports
          const cmConfig = {
            cm_api_host: config.public.CM_API_HOST,
            cm_partner_token: config.public.CM_PARTNER_TOKEN,
          };
          localStorage.setItem("cmConfig", JSON.stringify(cmConfig));
        }

        localStorage.setItem(
          "language",
          sessionData.data.attributes.current_language,
        );

        const currentSearchDashboard = JSON.parse(
          localStorage.getItem("currentSearchDashboard") as string,
        );
        if (
          sessionData.data.relationships.current_search_dashboard.data &&
          !currentSearchDashboard
        ) {
          localStorage.setItem(
            "currentSearchDashboard",
            JSON.stringify(
              sessionData.data.relationships.current_search_dashboard,
            ),
          );
        }

        const currentProfile = JSON.parse(
          localStorage.getItem("currentProfile") as string,
        );
        if (
          sessionData.data.relationships.current_profile.data &&
          !currentProfile
        ) {
          localStorage.setItem(
            "currentProfile",
            JSON.stringify(sessionData.data.relationships.current_profile),
          );
        }

        if (sessionData.included && sessionData.included.length > 0) {
          localStorage.setItem(
            "organization",
            JSON.stringify(sessionData.included[0]),
          );
        }

        // Mark session as validated
        markSessionValidated();

        return "Ok";
      }

      return "SessionDataMissing";
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error in setSessionData:", error);
      return String(error);
    } finally {
      // ⚠️ Important: only clear if it failed,
      // so a successful session stays cached
      sessionPromise = null;
    }
  })();

  return sessionPromise;
};
